// Watch mode, for React — `todo-takeout.md` entry 6.25.
//
// Key exports: useWatchMode, useIsWatching.
// Neighbours: lib/watchLock.ts (the record and the digest, which is where the
// lock actually lives), shell/AppRoutes.tsx (the route tree this switches),
// shell/WatchLayout.tsx (the locked screen), shell/Sidebar.tsx (the entry).
//
// **A view over `lib/watchLock.ts`, not a second home for the lock.** The
// write path refuses through that module because `serverFetch` is a function
// and cannot call a hook; this store exists so the shell re-renders when the
// lock changes. Two sources for one fact is how a hidden nav becomes the whole
// of a lock.

import { create } from 'zustand'

import {
  clearWatchLock,
  readWatchLock,
  setWatchLock,
  verifyWatchPassphrase,
  type WatchLock,
} from '@/lib/watchLock'

interface WatchModeState {
  /** Null while the portal is ordinary. */
  lock: WatchLock | null
  /**
   * Lock this browser on `panel`.
   *
   * Returns false when storage refused the write — a private window, or
   * storage full. **A screen that looks locked and is not is the one outcome
   * worth refusing**, because the operator walks away from it.
   */
  startWatching: (panel: string, passphrase: string) => Promise<boolean>
  /** Unlock, if `passphrase` is the one set. Returns false if it is not. */
  stopWatching: (passphrase: string) => Promise<boolean>
  /**
   * Drop the lock with no passphrase.
   *
   * For signing out, which is the documented way back for somebody who has
   * forgotten it. Never wire this to a control on the locked screen.
   */
  releaseWatch: () => void
  /** Re-read storage. For a lock set in another tab. */
  syncWatch: () => void
}

export const useWatchMode = create<WatchModeState>((set) => ({
  // Read at module load, so a reload lands locked rather than flashing the
  // portal for a frame first.
  lock: readWatchLock(),

  startWatching: async (panel, passphrase) => {
    const written = await setWatchLock(panel, passphrase)
    set({ lock: written })
    return written !== null
  },

  stopWatching: async (passphrase) => {
    if (!(await verifyWatchPassphrase(passphrase))) return false
    clearWatchLock()
    set({ lock: null })
    return true
  },

  releaseWatch: () => {
    clearWatchLock()
    set({ lock: null })
  },

  syncWatch: () => set({ lock: readWatchLock() }),
}))

/** True while this browser is showing one panel to a room. */
export function useIsWatching(): boolean {
  return useWatchMode((s) => s.lock !== null)
}
